import { useEffect, useMemo, useState } from 'react';

import { DEBOUNCE_MS } from '@/components/features/UsersList/constants';
import BaseMultiAutocomplete from '@/components/shared/Autocomplete/BaseMultiAutocomplete';
import { BaseMultiAutocompleteProps } from '@/components/shared/Autocomplete/types';
import { useAllocationGroupsAutocomplete } from '@/hooks/autocomplete/useAllocationGroupsAutocomplete';
import { EnhancedAllocationGroup } from '@/hooks/useManageUserAllocationGroups';
import { useUserAllocationGroupMembership } from '@/hooks/useUserAllocationGroupMembership';

export interface AllocationGroupAutocompleteForUserProps<AllocationGroup> {
  userId: string;
  organizationId: number;
  value: EnhancedAllocationGroup[];
  onChange: BaseMultiAutocompleteProps<AllocationGroup>['autocomplete']['onChange'];
  onApply?: NonNullable<BaseMultiAutocompleteProps<AllocationGroup>['actionButton']>['onApply'];
  disabled?: boolean;
  buttonDisabled?: boolean;
}

function AllocationGroupAutocompleteForUser(props: AllocationGroupAutocompleteForUserProps<EnhancedAllocationGroup>) {
  const [inputValue, setInputValue] = useState('');
  const [debouncedValue, setDebouncedValue] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(inputValue);
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [inputValue]);

  const { data: groups, isLoading: isLoadingGroups } = useAllocationGroupsAutocomplete({
    organizationId: props.organizationId,
    filter: debouncedValue,
    enabled: !props.disabled,
  });

  const groupIds = useMemo(() => groups?.map((g) => g.id).filter((id): id is number => id !== undefined) || [], [groups]);

  const {
    data: membershipData,
    isLoading: isLoadingMembership,
    isRefetching: isRefetchingMembership,
  } = useUserAllocationGroupMembership({
    userId: props.userId,
    organizationId: props.organizationId,
    groupIds,
    enabled: !props.disabled && groupIds.length > 0,
  });

  const enhancedOptions = useMemo<EnhancedAllocationGroup[]>(() => {
    if (!groups) return [];
    if (isLoadingMembership || isRefetchingMembership) return [];
    if (!membershipData)
      return groups.map((group) => ({
        ...group,
        isAssignedToUser: false,
      }));

    const membershipMap = new Map(membershipData.map((m) => [m.groupId, m]));

    return groups.map((group) => {
      const membership = group.id !== undefined ? membershipMap.get(group.id) : undefined;
      return {
        ...group,
        isAssignedToUser: membership?.isMember || false,
      };
    });
  }, [groups, membershipData, isLoadingMembership, isRefetchingMembership]);

  const handleInputChange = (_: React.SyntheticEvent, input: string) => {
    setInputValue(input);
  };

  const getOptionLabel = (option: EnhancedAllocationGroup) => option.name || '';

  const getOptionDisabled = (optionLabel: EnhancedAllocationGroup) => {
    if (isLoadingMembership || !membershipData) {
      return false;
    }
    const optionToDisable = enhancedOptions.find((g) => g.name === optionLabel.name);

    const isSelectedOptionDisabled = props.value.some((val) => val.name === optionLabel.name);

    return optionToDisable?.isAssignedToUser || isSelectedOptionDisabled;
  };

  return (
    <BaseMultiAutocomplete<EnhancedAllocationGroup>
      autocomplete={{
        options: enhancedOptions,
        value: props.value,
        inputValue: inputValue,
        loading: isLoadingGroups || isLoadingMembership || isRefetchingMembership,
        onChange: props.onChange,
        onInputChange: handleInputChange,
        getOptionLabel: getOptionLabel,
        getOptionDisabled: getOptionDisabled,
        label: 'Search Group to Add',
        disabled: props.disabled,
      }}
      actionButton={{
        buttonLabel: 'Add',
        onApply: props.onApply,
      }}
    />
  );
}

export default AllocationGroupAutocompleteForUser;
